import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Icon, ButtonMenu, Skeleton } from "@ann/web-components";
import { HeaderContainer, TitleContainer, IconWrapper } from "./Header.styled";
import { DeactivateWorkspace, ReactivateWorkspace, ChangeOwnerModal } from "../index";
import { useStore } from "../../../../../../../hooks";
import { useToast } from "../../../../../../../components/toast";

const Header = ({ title, isActive, workspaceId, isLoading = false }) => {
  const navigate = useNavigate();
  const { workspaceStore } = useStore();
  const toast = useToast();
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [isChangeOwnerModalOpen, setIsChangeOwnerModalOpen] = useState(false);
  const [isChangingOwner, setIsChangingOwner] = useState(false);

  const currentWorkspace = workspaceStore.selectedWorkspace;
  // Consideramos activo cuando isActive no es estrictamente false (undefined/null => activo por defecto)

  const handleBackClick = () => {
    navigate("/v2/workspace");
  };

  const handleMenuChange = (value) => {
    if (value === "deactivate-workspace") {
      setIsDeactivateModalOpen(true);
    } else if (value === "reactivate-workspace") {
      setIsReactivateModalOpen(true);
    } else if (value === "change-owner") {
      setIsChangeOwnerModalOpen(true);
    } else {
      console.log("Menu action:", value);
      // TODO: Implement other menu actions
    }
  };

  const handleDeactivateConfirm = async () => {
    setIsDeactivating(true);
    try {
      const workspace = workspaceStore.selectedWorkspace;
      if (!workspace) {
        toast.error({
          message: "No workspace selected",
          canClose: true,
        });
        setIsDeactivating(false);
        return;
      }

      // Use workspaceId from URL as fallback if workspace object doesn't have tenant_uuid
      const tenantUuid =
        workspace.tenant_uuid ||
        workspace.tenantUuid ||
        workspace.tenant_id ||
        workspace.tenantId ||
        workspace.id ||
        workspaceId; // Fallback to URL param

      if (!tenantUuid) {
        toast.error({
          message: "Missing tenant UUID for deactivation",
          canClose: true,
        });
        setIsDeactivating(false);
        return;
      }

      await workspaceStore.deactivateWorkspace(tenantUuid);

      // Priority: workspaceName/workspace_name (new) > tenantName/tenant_name (legacy) > name (generic) > fallback
      const workspaceName =
        workspaceStore.selectedWorkspace?.workspaceName ||
        workspaceStore.selectedWorkspace?.workspace_name ||
        workspaceStore.selectedWorkspace?.tenantName ||
        workspaceStore.selectedWorkspace?.tenant_name ||
        workspaceStore.selectedWorkspace?.name ||
        "Workspace";

      toast.success({
        title: workspaceName,
        message: "Workspace has been deactivated successfully.",
        canClose: true,
      });

      setIsDeactivateModalOpen(false);
    } catch (error) {
      const errorMessage =
        workspaceStore.errorMessage ||
        error.message ||
        "An error occurred while deactivating workspace.";

      toast.error({
        message: errorMessage,
        canClose: true,
      });
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleDeactivateModalClose = () => {
    if (!isDeactivating) {
      setIsDeactivateModalOpen(false);
    }
  };

  const handleReactivateConfirm = async () => {
    setIsReactivating(true);
    try {
      const workspace = workspaceStore.selectedWorkspace;
      if (!workspace) {
        toast.error({
          message: "No workspace selected",
          canClose: true,
        });
        setIsReactivating(false);
        return;
      }

      // Use workspaceId from URL as fallback if workspace object doesn't have tenant_uuid
      const tenantUuid =
        workspace.tenant_uuid ||
        workspace.tenantUuid ||
        workspace.tenant_id ||
        workspace.tenantId ||
        workspace.id ||
        workspaceId; // Fallback to URL param

      if (!tenantUuid) {
        toast.error({
          message: "Missing tenant UUID for reactivation",
          canClose: true,
        });
        setIsReactivating(false);
        return;
      }

      await workspaceStore.activateWorkspace(tenantUuid);

      // Priority: workspaceName/workspace_name (new) > tenantName/tenant_name (legacy) > name (generic) > fallback
      const workspaceName =
        workspaceStore.selectedWorkspace?.workspaceName ||
        workspaceStore.selectedWorkspace?.workspace_name ||
        workspaceStore.selectedWorkspace?.tenantName ||
        workspaceStore.selectedWorkspace?.tenant_name ||
        workspaceStore.selectedWorkspace?.name ||
        "Workspace";

      toast.success({
        title: `${workspaceName} reactivated`,
        message: "User access and active processes have been restored.",
        canClose: true,
      });

      setIsReactivateModalOpen(false);
    } catch (error) {
      const errorMessage =
        workspaceStore.errorMessage ||
        error.message ||
        "An error occurred while reactivating workspace.";

      toast.error({
        title: "Couldn’t reactivate the workspace",
        message: errorMessage,
        canClose: true,
      });
    } finally {
      setIsReactivating(false);
    }
  };

  const handleReactivateModalClose = () => {
    if (!isReactivating) {
      setIsReactivateModalOpen(false);
    }
  };

  const handleChangeOwnerConfirm = async (newOwnerEmail) => {
    setIsChangingOwner(true);
    try {
      const workspace = workspaceStore.selectedWorkspace;
      if (!workspace) {
        toast.error({
          message: 'No workspace selected',
          canClose: true,
        });
        setIsChangingOwner(false);
        return;
      }

      // Use workspaceId from URL as fallback
      const tenantUuid = workspace.tenant_uuid || workspace.tenantUuid || workspace.tenant_id || workspace.tenantId || workspaceId;
      // Priority: workspaceName/workspace_name (new) > tenantName/tenant_name (legacy) > name (generic) > fallback
      const tenantName = workspace.workspaceName || workspace.workspace_name || workspace.tenantName || workspace.tenant_name || workspace.name || title;
      // TODO: Remove hardcoded address when backend no longer requires it
      const address = workspace.address || 'Temporary Address';

      if (!tenantUuid || !tenantName) {
        toast.error({
          message: 'Missing required workspace data (tenant_uuid or tenant_name)',
          canClose: true,
        });
        setIsChangingOwner(false);
        return;
      }

      const workspaceData = {
        tenant_uuid: tenantUuid,
        tenant_name: tenantName,
        contact_email: newOwnerEmail.trim(),
        address: address,
      };

      console.log('[ChangeOwnerModal] Sending workspaceData:', workspaceData);
      await workspaceStore.updateWorkspace(workspaceData);
      
      // Modal close and success handling is done in ChangeOwnerModal via useEffect
      setIsChangeOwnerModalOpen(false);
    } catch (error) {
      console.error('Error changing owner:', error);
      // Error handling is done in ChangeOwnerModal via useEffect
    } finally {
      setIsChangingOwner(false);
    }
  };

  const handleChangeOwnerModalClose = () => {
    if (!isChangingOwner) {
      setIsChangeOwnerModalOpen(false);
    }
  };

  return (
    <HeaderContainer>
      <TitleContainer>
        <IconWrapper onClick={handleBackClick}>
          <Icon name="arrowLeft" size="medium" />
        </IconWrapper>
        {isLoading ? (
          <Skeleton height="32px" width="200px" />
        ) : (
          title && (
            <Typography category="title" size="large" weight="bold">
              {title}
            </Typography>
          )
        )}
      </TitleContainer>

      <ButtonMenu
        iconPosition="right"
        label="Actions"
        menuPosition="bottom"
        onChange={handleMenuChange}
        size="medium"
        type="gradient"
        width="auto"
      >
        <ButtonMenu.Menu>
          {isActive && (
            <ButtonMenu.Item
              label="Deactivate workspace"
              value="deactivate-workspace"
            />
          )}
          {!isActive && (
            <ButtonMenu.Item
              label="Reactivate workspace"
              value="reactivate-workspace"
            />
          )}
          <ButtonMenu.Item
            label="Change Owner"
            value="change-owner"
          />
        </ButtonMenu.Menu>
      </ButtonMenu>
            <DeactivateWorkspace
              isOpen={isDeactivateModalOpen}
              onClose={handleDeactivateModalClose}
              onConfirm={handleDeactivateConfirm}
              isLoading={isDeactivating}
              workspaceName={title}
            />
            <ReactivateWorkspace
              isOpen={isReactivateModalOpen}
              onClose={handleReactivateModalClose}
              onConfirm={handleReactivateConfirm}
              isLoading={isReactivating}
            />
            <ChangeOwnerModal
              isOpen={isChangeOwnerModalOpen}
              onClose={handleChangeOwnerModalClose}
              onConfirm={handleChangeOwnerConfirm}
              isLoading={isChangingOwner}
              workspace={workspaceStore.selectedWorkspace}
            />
    </HeaderContainer>
  );
};

export default Header;

